/**
 * Health-related utility functions for BMI calculations and unit conversions
 */

/**
 * Calculate BMI (Body Mass Index) from weight and height
 * @param {number} weightKg - Weight in kilograms
 * @param {number} heightMeters - Height in meters
 * @returns {string} BMI value rounded to 1 decimal place, or "N/A" if inputs are invalid
 * @example
 * calculateBMI(70, 1.75) // "22.9"
 * calculateBMI(null, 1.75) // "N/A"
 */
export const calculateBMI = (weightKg, heightMeters) => {
  if (!weightKg || !heightMeters || weightKg <= 0 || heightMeters <= 0) {
    return "N/A";
  }

  const bmi = weightKg / (heightMeters * heightMeters);
  return bmi.toFixed(1);
};

/**
 * Convert weight between different units
 * @param {number} value - Weight value to convert
 * @param {string} fromUnit - Source unit ('kg' or 'lbs')
 * @param {string} toUnit - Target unit ('kg' or 'lbs')
 * @returns {number} Converted weight value
 * @example
 * convertWeight(70, 'kg', 'lbs') // 154.32
 * convertWeight(154.32, 'lbs', 'kg') // 70.0
 */
export const convertWeight = (value, fromUnit, toUnit) => {
  if (!value || value <= 0) {
    return 0;
  }

  const KG_TO_LBS = 2.20462;

  // Normalize to lowercase
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();

  if (from === to) {
    return value;
  }

  if (from === "kg" && to === "lbs") {
    return value * KG_TO_LBS;
  }

  if (from === "lbs" && to === "kg") {
    return value / KG_TO_LBS;
  }

  return value;
};

/**
 * Convert height between different units
 * @param {number} value - Height value to convert
 * @param {string} fromUnit - Source unit ('m', 'cm', or 'ft')
 * @param {string} toUnit - Target unit ('m', 'cm', or 'ft')
 * @returns {number} Converted height value
 * @example
 * convertHeight(1.75, 'm', 'cm') // 175
 * convertHeight(175, 'cm', 'm') // 1.75
 * convertHeight(1.75, 'm', 'ft') // 5.74
 */
export const convertHeight = (value, fromUnit, toUnit) => {
  if (!value || value <= 0) {
    return 0;
  }

  const M_TO_CM = 100;
  const M_TO_FT = 3.28084;

  // Normalize to lowercase
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();

  if (from === to) {
    return value;
  }

  // Convert to meters first as base unit
  let meters = value;

  if (from === "cm") {
    meters = value / M_TO_CM;
  } else if (from === "ft") {
    meters = value / M_TO_FT;
  }

  // Convert from meters to target unit
  if (to === "m") {
    return meters;
  } else if (to === "cm") {
    return meters * M_TO_CM;
  } else if (to === "ft") {
    return meters * M_TO_FT;
  }

  return value;
};

/**
 * Format weight with unit for display
 * @param {number} value - Weight value
 * @param {string} unit - Unit ('kg' or 'lbs')
 * @returns {string} Formatted weight string
 * @example
 * formatWeightWithUnit(70.5, 'kg') // "70.5 kg"
 * formatWeightWithUnit(154.3, 'lbs') // "154.3 lbs"
 */
export const formatWeightWithUnit = (value, unit) => {
  if (!value || value <= 0) {
    return "N/A";
  }

  return `${value.toFixed(1)} ${unit}`;
};

/**
 * Format height with unit for display
 * @param {number} value - Height value
 * @param {string} unit - Unit ('m', 'cm', or 'ft')
 * @returns {string} Formatted height string
 * @example
 * formatHeightWithUnit(1.75, 'm') // "1.75 m"
 * formatHeightWithUnit(175, 'cm') // "175.0 cm"
 * formatHeightWithUnit(5.74, 'ft') // "5.74 ft"
 */
export const formatHeightWithUnit = (value, unit) => {
  if (!value || value <= 0) {
    return "N/A";
  }

  const decimals = unit?.toLowerCase() === "m" ? 2 : 1;
  return `${value.toFixed(decimals)} ${unit}`;
};
